// Trimmed from rakerone-web/src/components/files/vendor/file-upload.tsx: one
// file, no border-beam, no file list (the input panel shows the chosen file).
import { Upload } from 'lucide-react'
import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'

const EXT_FALLBACK: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.odt': 'application/vnd.oasis.opendocument.text',
  '.rtf': 'application/rtf',
  '.txt': 'text/plain',
  '.html': 'text/html',
  '.md': 'text/markdown',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.m4a': 'audio/mp4',
  '.mp4': 'audio/mp4',
  '.webm': 'audio/webm',
}

function effectiveType(file: File): string {
  if (file.type) return file.type.toLowerCase()
  const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
  return EXT_FALLBACK[ext] ?? ''
}

function matchesAccept(file: File, accept: string) {
  if (!accept) return true
  const type = effectiveType(file)
  return accept.split(',').some((raw) => {
    const token = raw.trim().toLowerCase()
    if (token.startsWith('.')) return file.name.toLowerCase().endsWith(token)
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1))
    return type === token
  })
}

export function Dropzone({
  accept,
  hint,
  onFile,
  className,
}: {
  accept: string
  hint: string
  onFile: (f: File) => void
  className?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const depth = useRef(0)
  const [dragging, setDragging] = useState(false)
  const [rejected, setRejected] = useState(false)

  const commit = (files: FileList | null) => {
    const file = files?.[0]
    if (!file || !matchesAccept(file, accept)) return setRejected(true)
    setRejected(false)
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload your own file"
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
        dragging
          ? 'border-primary bg-primary/5'
          : 'border-foreground/20 hover:border-foreground/40 hover:bg-muted/40',
        className,
      )}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault()
        depth.current++
        setDragging(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        if (--depth.current === 0) setDragging(false)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        depth.current = 0
        setDragging(false)
        commit(e.dataTransfer.files)
      }}
    >
      <Upload className="size-4 text-muted-foreground" />
      <div className="min-w-0">
        <div className="text-sm font-medium">
          {dragging ? 'Drop to run' : 'Drop your own file, or click to browse'}
        </div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      {rejected ? (
        <div className="rounded-md bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive">
          That file type is not supported here. Try {hint.split(' · ')[0]}.
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          commit(e.target.files)
          e.currentTarget.value = ''
        }}
      />
    </div>
  )
}
